import type { SortingState } from '@tanstack/react-table';
import { area, centerOfMass, convertArea, convertLength, distance, feature, length } from '@turf/turf';
import {
  type Agency,
  type DataRequestV2,
  Outcome,
  type Project,
  ProjectGeometryType,
  RequestStatus,
  UtilityPresence,
} from '~/common/types';

export const DAYS_PER_WEEK = 7;

/** Hover copy shown over the est-time "—" when an agency has no usable response-time stats yet. */
export const NO_ESTIMATE_TOOLTIP = 'Not enough historical requests yet to estimate response time.';

/** Hover copy over the est-time "—" once every request on the row has finished. */
export const COMPLETED_REQUEST_TOOLTIP = 'This request has been completed.';

// Half-open ranges: a bucket matches when weeks < maxWeeks and no earlier bucket matched.
// Boundaries are a best-guess pending product alignment.
export const ESTIMATED_RESPONSE_TIME_BUCKETS = [
  { maxWeeks: 1, label: '< 1 week' },
  { maxWeeks: 3, label: '1–3 weeks' },
  { maxWeeks: 5, label: '3–5 weeks' },
  { maxWeeks: 8, label: '5–8 weeks' },
  { maxWeeks: 12, label: '8–12 weeks' },
  { maxWeeks: Number.POSITIVE_INFINITY, label: '12+ weeks' },
] as const;

/**
 * The agency-level response time in days, or null when the agency has no usable stats.
 *
 * Reads the `calculatedResponseTime` column (already in days) off the agency-level active row. The
 * column is NOT NULL in the DB, but the value is still validated because `responseStats` is absent
 * entirely on payloads that do not select it. There is a single estimate per agency —
 * `calculatedResponseTime` has no as-builts counterpart, so maps and as-builts requests share it.
 */
export function getAgencyResponseTimeDays(agency: Pick<Agency, 'responseStats'>): number | null {
  const agencyLevelRow = agency.responseStats?.find((s) => s.agencySubGeometryId === null && s.status === 'active');
  const days = agencyLevelRow?.calculatedResponseTime;
  return typeof days === 'number' && Number.isFinite(days) && days >= 0 ? days : null;
}

/** The bucket label for an agency's response time, or null when it has no usable stats. */
export function getEstimatedResponseTimeLabel(agency: Pick<Agency, 'responseStats'>): string | null {
  const days = getAgencyResponseTimeDays(agency);
  if (days === null) return null;
  const weeks = days / DAYS_PER_WEEK;
  const bucket = ESTIMATED_RESPONSE_TIME_BUCKETS.find((b) => weeks < b.maxWeeks);
  return bucket?.label ?? null;
}

/** How much historical data backs an agency's estimate, used to colour the estimate pill. */
export type ResponseConfidence = 'high' | 'established' | 'low';

// Bands by historical response count, highest threshold first.
export const CONFIDENCE_BANDS = [
  { minResponses: 30, confidence: 'high' },
  { minResponses: 11, confidence: 'established' },
  { minResponses: 0, confidence: 'low' },
] as const satisfies ReadonlyArray<{ minResponses: number; confidence: ResponseConfidence }>;

/**
 * The agency-level historical response count (`stats.n_responses`), or null when there is no usable
 * agency-level row. Sub-geometry and archived rows are ignored, matching the estimate helpers above.
 */
export function getAgencyResponseCount(agency: Pick<Agency, 'responseStats'>): number | null {
  const agencyLevelRow = agency.responseStats?.find((s) => s.agencySubGeometryId === null && s.status === 'active');
  const value = agencyLevelRow?.stats?.n_responses;
  return typeof value === 'number' && Number.isFinite(value) && value >= 0 ? value : null;
}

/** The confidence tier for an agency's estimate, or null when there is no usable response count. */
export function getResponseConfidence(agency: Pick<Agency, 'responseStats'>): ResponseConfidence | null {
  const count = getAgencyResponseCount(agency);
  if (count === null) return null;
  return CONFIDENCE_BANDS.find((band) => count >= band.minResponses)?.confidence ?? null;
}

// Sort rank for confidence tiers (higher = more confident); unknown/no-count tier ranks lowest.
const CONFIDENCE_SORT_RANK: Record<ResponseConfidence, number> = { high: 3, established: 2, low: 1 };

/** Numeric rank for sorting by confidence tier (higher = more confident); unknown tier = 0. */
export function getResponseConfidenceRank(agency: Pick<Agency, 'responseStats'>): number {
  const confidence = getResponseConfidence(agency);
  return confidence === null ? 0 : CONFIDENCE_SORT_RANK[confidence];
}

/** A data request as the est-time cell reads it. */
export type EstTimeRequest = Pick<
  DataRequestV2,
  'type' | 'requestStatus' | 'createdAt' | 'utilityPresence' | 'outcome'
>;

/**
 * What the Est time cell shows for one agency row.
 *
 * - `suppressed`: per-agency CivilSearch killswitch, a bare dash with no explanation.
 * - `estimate`: the agency has usable stats and nothing is in flight against it.
 * - `noEstimate`: no usable stats and nothing is in flight.
 * - `requested`: at least one request is still in flight. The cell shows the date it was placed and
 *   the range moves to the hover.
 * - `delivered`: nothing is in flight and at least one completed request received documents.
 * - `completed`: nothing is in flight and the row's completed requests closed without documents
 *   (no assets present, agency will not share, unresponsive, referred, outside service area).
 *
 * Cancelled requests leave no trace: a row whose requests were all cancelled reads as `estimate` or
 * `noEstimate`, exactly as if nothing had been requested.
 */
export type EstTimeState =
  | { kind: 'suppressed' }
  | { kind: 'noEstimate' }
  | { kind: 'delivered' }
  | { kind: 'completed' }
  | { kind: 'estimate'; label: string; days: number; confidence: ResponseConfidence | null }
  | {
      kind: 'requested';
      estimate: string | null;
      days: number | null;
      requestedOnLabel: string;
      confidence: ResponseConfidence | null;
    };

/**
 * The range a row surfaces, in days, or null when it has none. Requested rows count: their range is
 * hover-only, but it is the same estimate and the Est time sort modes rank by it.
 */
export function surfacedEstimateDays(state: EstTimeState): number | null {
  if (state.kind === 'estimate') return state.days;
  return state.kind === 'requested' ? state.days : null;
}

// Date format for the "Requested on" value: month-first, per product.
const REQUESTED_ON_FORMAT = 'MM/dd/yy';

/** Whether a request is still open, i.e. neither completed nor cancelled/dismissed. */
function isInFlight(status: RequestStatus): boolean {
  return status !== RequestStatus.Complete && status !== RequestStatus.Cancelled && status !== RequestStatus.Dismissed;
}

/** Whether a completed request closed with documents in hand, the one outcome that reads as delivered. */
function isDocumentsReceived(request: EstTimeRequest): boolean {
  return request.utilityPresence === UtilityPresence.Present && request.outcome === Outcome.DocumentsReceived;
}

/**
 * The Est time cell state for an agency row, given the requests that belong to that row. In-flight
 * requests win over terminal ones, so an agency mid-way through a second request still reads as
 * requested, as does a row with the maps request delivered and the as-builts one still open. The date
 * is the earliest in-flight request's, matching what the customer placed first. The estimate is the
 * agency's current range, recomputed rather than read back from the value snapshotted onto the
 * request at submission.
 */
export function getEstTimeState(
  agency: Pick<Agency, 'responseStats' | 'hideResponseEstimate'>,
  requests: readonly EstTimeRequest[],
): EstTimeState {
  // Per-agency killswitch: CivilSearch can suppress the customer-facing estimate entirely.
  if (agency.hideResponseEstimate) return { kind: 'suppressed' };

  const inFlight = requests.filter((r) => isInFlight(r.requestStatus));
  if (inFlight.length > 0) {
    const requestedAt = inFlight.reduce(
      (earliest, r) => (r.createdAt < earliest ? r.createdAt : earliest),
      inFlight[0].createdAt,
    );
    return {
      kind: 'requested',
      estimate: getEstimatedResponseTimeLabel(agency),
      days: getAgencyResponseTimeDays(agency),
      requestedOnLabel: `Requested on ${requestedAt.toFormat(REQUESTED_ON_FORMAT)}`,
      confidence: getResponseConfidence(agency),
    };
  }

  const completed = requests.filter((r) => r.requestStatus === RequestStatus.Complete);
  if (completed.some(isDocumentsReceived)) return { kind: 'delivered' };
  if (completed.length > 0) return { kind: 'completed' };

  const days = getAgencyResponseTimeDays(agency);
  const label = getEstimatedResponseTimeLabel(agency);
  if (days === null || label === null) return { kind: 'noEstimate' };
  return { kind: 'estimate', label, days, confidence: getResponseConfidence(agency) };
}

// The Est time header cycles the sort: default → by est time (asc) → by confidence tier (desc) → default.
// Shared by the utilities table and the Select Assets step so both surfaces cycle identically. Relies on
// the hidden `estTimeDays` / `estTimeConfidence` helper columns being present in both tables.
const AGENCY_NAME_SORT: SortingState = [{ id: 'agencyName', desc: false }];
export function nextEstTimeSort(current: SortingState): SortingState {
  const activeId = current[0]?.id;
  if (activeId === 'estTimeDays') return [{ id: 'estTimeConfidence', desc: true }];
  if (activeId === 'estTimeConfidence') return AGENCY_NAME_SORT;
  return [{ id: 'estTimeDays', desc: false }];
}

// Project shapes where the response-time estimate is unreliable, so the whole Est time column is hidden.
const UNRELIABLE_LINEAR_MILES = 3;
const UNRELIABLE_TOTAL_POLYGON_SQ_MILES = 1;
const UNRELIABLE_POLYGON_COUNT = 3;
const UNRELIABLE_POLYGON_SEPARATION_MILES = 1;

/**
 * True when any two polygons in the MultiPolygon have centers farther apart than `miles`. Uses the
 * area-weighted `centerOfMass` rather than the vertex mean, so the separation reflects where the polygons
 * actually are, not how densely their boundaries were digitized (or where holes sit).
 */
function anyPolygonsFartherApart(geometry: GeoJSON.MultiPolygon, miles: number): boolean {
  const centers = geometry.coordinates.map((coordinates) => centerOfMass({ type: 'Polygon', coordinates }));
  for (let i = 0; i < centers.length; i++) {
    for (let j = i + 1; j < centers.length; j++) {
      if (distance(centers[i], centers[j], { units: 'miles' }) > miles) return true;
    }
  }
  return false;
}

/**
 * Whether a project's geometry makes response-time estimates unreliable, in which case the Est time
 * column is hidden entirely (no messaging). Linear projects are judged by centerline length; polygon
 * projects by total area, polygon count, and how far apart the polygons are. Circle and small (3ac)
 * projects, and projects with no geometry, are always treated as reliable.
 */
export function isUnreliableProjectShape(
  project: Pick<Project, 'geometry' | 'geometryType' | 'linear'> | undefined,
): boolean {
  if (!project?.geometry) return false;

  if (project.geometryType === ProjectGeometryType.Linear) {
    const centerline = project.linear?.geometry;
    if (!centerline) return false;
    // Compare raw (unrounded) miles: rounding could pull a shape just over the threshold back under it.
    const lengthMiles = convertLength(length(feature(centerline)), 'kilometers', 'miles');
    return lengthMiles > UNRELIABLE_LINEAR_MILES;
  }

  if (project.geometryType === ProjectGeometryType.Polygon) {
    // Raw (unrounded) square miles, for the same boundary-precision reason as the linear check. `area()`
    // totals every polygon in the MultiPolygon, so a large site hides the column whether it is drawn as
    // one polygon or several adjacent ones (per product).
    const totalAreaSqMiles = convertArea(area(project.geometry), 'meters', 'miles');
    if (totalAreaSqMiles > UNRELIABLE_TOTAL_POLYGON_SQ_MILES) return true;

    const polygonCount = project.geometry.coordinates.length;
    return (
      polygonCount > UNRELIABLE_POLYGON_COUNT ||
      anyPolygonsFartherApart(project.geometry, UNRELIABLE_POLYGON_SEPARATION_MILES)
    );
  }

  // Circle, Small_3ac, or unknown geometry types: estimate is treated as reliable.
  return false;
}
